import { useEffect, useCallback, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export default function Lightbox({ items, index, onClose, onNav }) {
  const [zoomed, setZoomed] = useState(false);
  const containerRef = useRef(null);
  const item = items[index];

  const keyHandler = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    },
    [onClose, onNav]
  );

  useEffect(() => {
    window.addEventListener("keydown", keyHandler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", keyHandler);
      document.body.style.overflow = "";
    };
  }, [keyHandler]);

  useEffect(() => {
    setZoomed(false);
  }, [index]);

  if (!item) return null;

  return (
    <motion.div
      data-testid="lightbox-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex flex-col bg-black/95 backdrop-blur-sm"
    >
      <div className="flex items-center justify-between px-5 py-4 text-white">
        <p className="text-xs uppercase tracking-[0.25em] text-white/70">
          {String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
        </p>
        <button
          data-testid="lightbox-close-button"
          aria-label="Close lightbox"
          onClick={onClose}
          className="rounded-full border border-white/30 p-2 transition-colors duration-200 hover:bg-white/10"
        >
          <X size={20} />
        </button>
      </div>

      <div
        ref={containerRef}
        className="relative flex flex-1 items-center justify-center overflow-hidden px-4"
      >
        <button
          data-testid="lightbox-prev-button"
          aria-label="Previous photo"
          onClick={() => onNav(-1)}
          className="absolute left-4 z-10 rounded-full border border-white/30 bg-black/40 p-3 text-white transition-colors duration-200 hover:bg-white/10"
        >
          <ChevronLeft size={22} />
        </button>
        <AnimatePresence mode="wait">
          <motion.img
            key={item.url}
            src={item.url}
            alt={item.title}
            data-testid="lightbox-image"
            onDoubleClick={() => setZoomed((z) => !z)}
            drag={zoomed}
            dragConstraints={containerRef}
            dragElastic={0.15}
            animate={{ opacity: 1, scale: zoomed ? 2 : 1 }}
            initial={{ opacity: 0, scale: zoomed ? 2 : 0.98 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className={`max-h-[70vh] max-w-full rounded-2xl object-contain ${
              zoomed ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in"
            }`}
          />
        </AnimatePresence>
        <button
          data-testid="lightbox-next-button"
          aria-label="Next photo"
          onClick={() => onNav(1)}
          className="absolute right-4 z-10 rounded-full border border-white/30 bg-black/40 p-3 text-white transition-colors duration-200 hover:bg-white/10"
        >
          <ChevronRight size={22} />
        </button>
        <p className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-4 py-1.5 text-[11px] uppercase tracking-[0.18em] text-white/85 backdrop-blur-sm">
          {zoomed ? "Drag to pan — double-click to zoom out" : "Double-click / double-tap to zoom"}
        </p>
      </div>

      <div className="px-5 pb-4">
        <h3 className="font-display text-lg font-semibold text-white">{item.title}</h3>
        <p className="text-sm text-white/70">{item.desc}</p>
        <div className="mt-4 flex gap-2 overflow-x-auto pb-2">
          {items.map((g, i) => (
            <button
              key={g.url}
              data-testid={`lightbox-thumb-${i}`}
              onClick={() => onNav(i - index)}
              className={`h-14 w-20 flex-none overflow-hidden rounded-lg border-2 transition-colors duration-200 ${
                i === index ? "border-bronze" : "border-transparent opacity-60 hover:opacity-100"
              }`}
            >
              <img src={g.url} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
